import type { CaseSettingsInputs } from '@/stores/caseSettings/types';
import {
    type CaseSettingsApiObjectCreate,
    type CaseSettingsOptions,
    getCaseSettings,
    type OptionsApiResponse,
} from '@/api/caseSettings';
import { verify } from '@/util/verify';
import { mapToState } from '@/stores/caseSettings/mapToState';
import { ById, ByName } from '@/util/sorting';
import { getPreferences, NoSurgicalPreferencesError, type THAPreferences } from '@/api/preferences';

import { client } from '@/api/http';
import { AxiosError, type AxiosResponse, HttpStatusCode } from 'axios';
import { isEmpty } from 'lodash';
import { caseSettingsApiObjectCreate } from '@/stores/caseSettings/caseSettingsApiObjectCreate';

export async function loadCaseSettings(id: number): Promise<CaseSettingsInputs> {
    const options = await loadCaseSettingsOptions();
    const caseSettings = await getCaseSettings(id);

    return mapToState(caseSettings, options);
}

export async function saveCaseSettings(
    isNewCase: boolean,
    caseSettings: CaseSettingsInputs,
    webComponentVersion: string,
): Promise<number> {
    const body: CaseSettingsApiObjectCreate = caseSettingsApiObjectCreate(caseSettings, webComponentVersion);

    if (isNewCase) {
        return await _createCaseSettings(body);
    } else {
        const caseId = verify(caseSettings.id, 'case id is required');
        await _updateCaseSettings(caseId, body);
        return caseId;
    }
}

export function mapOptionsToState(data: OptionsApiResponse) {
    return {
        stems: [...data.options.stems].sort(ById),
        surgeons: [...data.options.surgeons].sort(ByName),
    };
}

export async function loadCaseSettingsOptions(): Promise<CaseSettingsOptions> {
    const data = await _getCaseSettingsOptions();
    return mapOptionsToState(data);
}

export async function getSurgeonPreferences(surgeonId: number): Promise<THAPreferences | null> {
    try {
        return await getPreferences(surgeonId);
    } catch (err) {
        if (err instanceof NoSurgicalPreferencesError) {
            return null;
        } else {
            throw err;
        }
    }
}

function _isValidApiResponse({ options }: OptionsApiResponse): boolean {
    return !isEmpty(options?.stems) && !isEmpty(options?.surgeons);
}

async function _getCaseSettingsOptions(): Promise<OptionsApiResponse> {
    return client
        .get('/cases/options')
        .then(({ status, data }: AxiosResponse<OptionsApiResponse>) => {
            if (status === HttpStatusCode.Ok && _isValidApiResponse(data)) {
                return data;
            } else {
                throw Error('Failed to load case settings options');
            }
        });
}

// Private function, only exported for testing
export async function _createCaseSettings(body: any): Promise<number> {
    return client.post('/cases/', body).then(({ status, data }: AxiosResponse) => {
        if (status === HttpStatusCode.Created) {
            return data?.id;
        }

        throw new AxiosError(`Failed to save case settings: ${data?.detail}`);
    });
}

async function _updateCaseSettings(caseId: number, body: any): Promise<void> {
    return client
        .put(`/cases/${caseId}/settings`, body, { timeout: 10 * 1000 })
        .then(({ status, data }: AxiosResponse) => {
            if (status === HttpStatusCode.Accepted) {
                return;
            }

            throw new AxiosError(`Failed to update case settings: ${data?.detail}`);
        });
}
